import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Img from '../../../../../components/Img';
import PopOverMenu from '../../../../../components/PopOverMenu';
import MatchPositionContent from './MatchPositionContent';
import ContactsInfoPopup from '../../../../../components/PopUps/ContactsInfoPopup';
import { useStatsig } from '../../../../../components/hooks/useStatsig';
import { isOrganizer, isDoublesFormat } from '../../../../../utils/events';
import { eventFormatTypeIds, drawPositionTypeIds } from '../../../../../utils/eventConstants';
import { EventProfileMatchCardMenuOptions, PositionNumbers } from '../../../../../utils/constants';
import {
    getRoundLabelForMatchCard,
    getMatchWinnerPositions,
    getMatchStatusData,
    getMatchOutcomeLabel,
    getStatusBarDisplayItems,
    getDataBarDisplayItems,
    getDrawDisplayName,
} from '../../../../../utils/matchCardUtils';

import styles from './eventProfileMatchCard.scss';

const EventProfileMatchCard = (props) => {
    const auth = useSelector(({ auth }) => auth);
    const { checkGate, featureGates } = useStatsig();
    const isLiveScoresEnabled = checkGate(featureGates.WEB_PTT_LIVE_SCORES);

    const {
        match = {},
        scheduleInfo,
        matchToLastTimedMatchMap,
        draw,
        event,
        roundSize,
        postScoreEnabled,
        onPostScoreClick,
        onMatchCardMenuSelect,
        showFacilityCourtInfo = false,
        showMatchDate = false,
        showDrawName = false,
        showRoundLevel = false,
        showContactFooter,
        showPostScoreForParticipant = false,
        'data-testid': dataTestId,
    } = props;

    const isCurrentMemberAdmin = !!(auth?.user && event && isOrganizer(event));
    const timeZoneValue = event?.eventSchedule?.timeZone?.value || 'UTC';

    const drawFormatTypeId = draw?.formatTypeId ?? (match.position1?.typeId === drawPositionTypeIds.TEAM ? eventFormatTypeIds.DOUBLES : eventFormatTypeIds.SINGLES);
    const isDoubles = isDoublesFormat(drawFormatTypeId);
    const participatingMembers = match?.participatingMembers ?? [];
    const [didPosition1Win, didPosition2Win] = getMatchWinnerPositions(match, isLiveScoresEnabled);
    const [showContactsModal, setShowContactsModal] = useState(false);
    const positionsList = [{
        id: PositionNumbers.POSITION_1,
        value: PositionNumbers.POSITION_1,
        key: 'position1',
        hasPositionWon: didPosition1Win,
        seedKey: 'position1Seed',
        liveScoreKey: 'home_score',
        liverServeStatusKey: 'home',
    }, {
        id: PositionNumbers.POSITION_2,
        value: PositionNumbers.POSITION_2,
        key: 'position2',
        hasPositionWon: didPosition2Win,
        seedKey: 'position2Seed',
        liveScoreKey: 'away_score',
        liverServeStatusKey: 'away',
    }];

    const matchStatusData = getMatchStatusData(match, timeZoneValue, scheduleInfo, matchToLastTimedMatchMap, isLiveScoresEnabled);

    const isCompleted = matchStatusData.status === 'completed';

    const menuOptions = useMemo(() => {
        const options = [];
        if (isCurrentMemberAdmin && postScoreEnabled && match?.result) {
            options.push(EventProfileMatchCardMenuOptions.EDIT_SCORE);
            options.push(EventProfileMatchCardMenuOptions.DELETE_SCORE);
        }
        return options;
    }, [isCurrentMemberAdmin, postScoreEnabled, match?.result]);

    const hasBothPositions = match.position1 && match.position2;
    const hasBye = (match.position1?.typeId === drawPositionTypeIds.BYE) || (match.position2?.typeId === drawPositionTypeIds.BYE);
    const noScorePosted = !match?.result?.score && !isCompleted;
    const showPostScoreButton = (isCurrentMemberAdmin || showPostScoreForParticipant) && postScoreEnabled && hasBothPositions && noScorePosted && !hasBye;

    const matchOutcomeLabel = match?.result ? getMatchOutcomeLabel(match.result) : null;

    const roundLabel = (showRoundLevel && draw?.eliminationTypeId != null && roundSize != null)
        ? getRoundLabelForMatchCard(draw.eliminationTypeId, match?.roundNumber, roundSize, match?.matchPosition)
        : { label: `R${match.roundNumber ?? ''}`, match: null };

    const showTimeBased = !hasBye && (match?.isTimeBased ?? draw?.isTimeBased);
    const statusBarDisplayItems = getStatusBarDisplayItems(matchStatusData, match, showMatchDate, showTimeBased);
    const statusBarDisplayItemsLength = statusBarDisplayItems?.length || 0;
    const drawDisplayName = getDrawDisplayName(draw);
    const dataBarDisplayItems = getDataBarDisplayItems(match, roundLabel, {
        showDrawName,
        showFacilityCourtInfo,
        drawDisplayName,
        event,
        draw,
    });
    const dataBarDisplayItemsLength = dataBarDisplayItems?.length || 0;

    const handlePostScoreClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onPostScoreClick) {
            onPostScoreClick(match);
        }
    };

    const handleMatchCardMenuSelect = (menuOption) => {
        if (onMatchCardMenuSelect) {
            onMatchCardMenuSelect(menuOption, match);
        }
    };

    const handleContactFooterClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowContactsModal(true);
    };

    return (
        <div
            className={`${styles.eventProfileMatchCard}${isDoubles ? ` ${styles.doubles}` : ''}${showContactFooter ? ` ${styles.withContactFooter}` : ''}`}
            data-testid={dataTestId}
        >
            <div className={`${styles.statusBar} ${styles[matchStatusData?.className]}`} data-testid={dataTestId && `${dataTestId}.status-bar`}>
                <div className={styles.statusBarLeft}>
                    {statusBarDisplayItems?.map((item) => (
                        <span key={item.key}>
                            {item.type === 'text' && (
                                <span
                                    className={`${styles[item.className]} ${item.key === statusBarDisplayItems[0]?.key ? styles.bold : ''}`}
                                >
                                    {item.value}
                                </span>
                            )}
                            {item.type === 'image' && (
                                <Img
                                    className={styles[item.className]}
                                    src={item.src}
                                    title={item.title}
                                />
                            )}
                            {statusBarDisplayItemsLength > 1 && item.key !== statusBarDisplayItems[statusBarDisplayItems.length - 1]?.key && (
                                <span className={styles.separator}> | </span>
                            )}
                        </span>
                    ))}
                </div>
                <div className={styles.statusBarRight}>
                    {matchOutcomeLabel && (
                        <span className={styles.matchOutcomeLabel} data-testid={dataTestId && `${dataTestId}.match-outcome`}>{matchOutcomeLabel}</span>
                    )}
                    {!matchOutcomeLabel && showPostScoreButton && (
                        <button
                            type="button"
                            className={styles.postScoreButton}
                            disabled={!postScoreEnabled}
                            onClick={handlePostScoreClick}
                            data-testid={dataTestId && `${dataTestId}.post-score-btn`}
                        >
                            <Img src="/img/icons/v4/add-post-score.svg" className={styles.postScoreIcon} />
                            Post Score
                        </button>
                    )}
                    {menuOptions.length > 0 && match?.result && (
                        <div className={styles.menuContainer}>
                            <PopOverMenu
                                horizontalEllipsis={false}
                                menuItems={menuOptions}
                                onSelected={handleMatchCardMenuSelect}
                                anchorClass="pt4"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.dataBar}>
                <div className={styles.dataBarLeft}>
                    {dataBarDisplayItems?.map((item) => (
                        <span key={item.key}>
                            {item.type === 'link' && (
                                <Link to={item.href} className={styles[item.className]}>
                                    {item.value}
                                </Link>
                            )}
                            {item.type === 'text' && (
                                <span className={styles[item.className]}>{item.value}</span>
                            )}
                            {dataBarDisplayItemsLength > 1 && item.key !== dataBarDisplayItems[dataBarDisplayItems.length - 1]?.key && (
                                <span className={styles.separator}> | </span>
                            )}
                        </span>
                    ))}
                </div>
                <div className={styles.dataBarRight}>
                    {matchStatusData.matchDuration && (
                        <span className={styles.matchDuration} data-testid={dataTestId && `${dataTestId}.match-duration`}>{matchStatusData.matchDuration}</span>
                    )}
                </div>
            </div>
            <div className={styles.cardContent}>
                {positionsList.map((position) => (
                    <MatchPositionContent
                        key={position.key}
                        position={position}
                        hasPositionWon={position.hasPositionWon}
                        match={match}
                        draw={draw}
                        event={event}
                        showSeparator={position.id === PositionNumbers.POSITION_1}
                        isDoubles={isDoubles}
                        isLiveScoresEnabled={isLiveScoresEnabled}
                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                    />
                ))}
            </div>
            {showContactFooter && (
                <div className={styles.contactFooter}>
                    <button
                        type="button"
                        className={styles.contactFooterButton}
                        onClick={handleContactFooterClick}
                        data-testid={dataTestId && `${dataTestId}.contact-btn`}
                    >
                        <Img
                            src="/img/v4/icon-16px-contact-chat.svg"
                            className={styles.contactFooterIcon}
                            alt=""
                        />
                        <span>Contact</span>
                    </button>
                </div>
            )}
            {showContactsModal && (
                <ContactsInfoPopup
                    onClose={() => setShowContactsModal(false)}
                    position1MemberIds={match?.position1?.memberIds}
                    position2MemberIds={match?.position2?.memberIds}
                    participatingMembers={participatingMembers}
                />
            )}
        </div>
    );
};

EventProfileMatchCard.propTypes = {
    match: PropTypes.shape({
        position1: PropTypes.object,
        position2: PropTypes.object,
        position1Seed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        position2Seed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        result: PropTypes.object,
        participatingMembers: PropTypes.array,
        facilityAbbreviation: PropTypes.string,
        courtName: PropTypes.string,
        isLive: PropTypes.bool,
        liveMatch: PropTypes.object,
        isTimeBased: PropTypes.bool,
        roundNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    scheduleInfo: PropTypes.shape({
        courtId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        includesTime: PropTypes.bool,
        notBefore: PropTypes.bool,
        sequenceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        matchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    matchToLastTimedMatchMap: PropTypes.objectOf(PropTypes.any),
    draw: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        groupName: PropTypes.string,
        formatTypeId: PropTypes.number,
        ballTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        eliminationTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        roundSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        matchPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    event: PropTypes.shape({}),
    roundSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    postScoreEnabled: PropTypes.bool,
    onPostScoreClick: PropTypes.func,
    onMatchCardMenuSelect: PropTypes.func,
    showFacilityCourtInfo: PropTypes.bool,
    showMatchDate: PropTypes.bool,
    showDrawName: PropTypes.bool,
    showRoundLevel: PropTypes.bool,
    showContactFooter: PropTypes.bool,
    showPostScoreForParticipant: PropTypes.bool,
    'data-testid': PropTypes.string,
};

EventProfileMatchCard.defaultProps = {
    scheduleInfo: null,
    draw: null,
    event: null,
    roundSize: null,
    postScoreEnabled: false,
    onPostScoreClick: null,
    'data-testid': null,
    onMatchCardMenuSelect: null,
    showFacilityCourtInfo: false,
    showMatchDate: false,
    showDrawName: false,
    showRoundLevel: false,
    matchToLastTimedMatchMap: {},
    showContactFooter: false,
    showPostScoreForParticipant: false,
};

export default EventProfileMatchCard;
